import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const AISidecar = ({ isOpen, onToggle, currentQuestion }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState('gpt-4');
  const messagesEndRef = useRef(null);

  const models = [
    { id: 'gpt-4', name: 'GPT-4', description: 'Most capable model' },
    { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo', description: 'Fast and efficient' },
    { id: 'claude-3', name: 'Claude 3', description: 'Anthropic model' }
  ];

  const suggestedQueries = [
    "What are the key considerations for this SAP process?",
    "How does this decision impact downstream processes?",
    "What are common implementation challenges?",
    "Explain the business benefits of this configuration"
  ];

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Initialize with welcome message
    if (messages?.length === 0) {
      setMessages([{
        id: 1,
        type: 'assistant',
        content: `Hello! I'm your SAP implementation assistant. I can help you understand the current question, provide context about SAP processes, and guide you through implementation considerations.\n\nFeel free to ask me anything about the questionnaire or SAP Best Practices.`,
        timestamp: new Date()?.toLocaleTimeString()
      }]);
    }
  }, []);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!inputValue?.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputValue,
      timestamp: new Date()?.toLocaleTimeString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    // Simulate AI response
    setTimeout(() => {
      const aiResponse = {
        id: Date.now() + 1,
        type: 'assistant',
        content: generateAIResponse(inputValue),
        timestamp: new Date()?.toLocaleTimeString(),
        model: selectedModel
      };
      
      setMessages(prev => [...prev, aiResponse]);
      setIsLoading(false);
    }, 1500);
  };

  const generateAIResponse = (query) => {
    const responses = [
      `Based on your question about "${query}", here are the key considerations:\n\n• This process typically involves multiple stakeholders across finance and operations\n• Configuration decisions here will impact reporting and analytics capabilities\n• Consider your organization's approval workflows and segregation of duties\n• Integration with existing systems may require custom development\n\nWould you like me to elaborate on any of these points?`,
      
      `Great question! For this SAP process:\n\n• The standard Best Practice provides a solid foundation\n• Your organization size (${currentQuestion?.organizationContext?.size || 'Enterprise'}) suggests you'll need enhanced controls\n• Consider the impact on month-end closing processes\n• Training requirements for end users should be factored in\n\nThis decision will influence the Fit/Gap analysis in the next phase.`,
      
      `This is a critical configuration point. Here's what you should consider:\n\n• Regulatory requirements in your industry may dictate certain settings\n• The choice here affects system performance and user experience\n• Integration touchpoints with other modules need to be evaluated\n• Change management implications for your current processes\n\nI recommend documenting the rationale for your decision for the BPD² document.`
    ];
    
    return responses?.[Math.floor(Math.random() * responses?.length)];
  };

  const handleSuggestedQuery = (query) => {
    setInputValue(query);
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage();
    }
  };

  if (!isOpen) {
    return (
      <div className="fixed right-4 top-1/2 transform -translate-y-1/2 z-50">
        <Button
          variant="default"
          size="icon"
          onClick={onToggle}
          className="w-12 h-12 rounded-full shadow-elevation-2"
        >
          <Icon name="Bot" size={24} />
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed right-0 top-16 bottom-0 w-96 bg-card border-l border-border shadow-elevation-2 z-40 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Bot" size={20} className="text-primary" />
          <h2 className="text-lg font-semibold text-foreground">AI Assistant</h2>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
        >
          <Icon name="X" size={20} />
        </Button>
      </div>
      {/* Model Selection */}
      <div className="p-3 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Cpu" size={16} className="text-muted-foreground" />
          <select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e?.target?.value)}
            className="text-sm bg-background border border-border rounded px-2 py-1 text-foreground"
          >
            {models?.map((model) => (
              <option key={model?.id} value={model?.id}>
                {model?.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages?.map((message) => (
          <div
            key={message?.id}
            className={`flex ${message?.type === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-lg p-3 ${
                message?.type === 'user' ?'bg-primary text-primary-foreground' :'bg-muted text-foreground'
              }`}
            >
              <div className="text-sm whitespace-pre-wrap">
                {message?.content}
              </div>
              <div className="flex items-center justify-between mt-2 text-xs opacity-70">
                <span>{message?.timestamp}</span>
                {message?.model && (
                  <span className="flex items-center space-x-1">
                    <Icon name="Cpu" size={10} />
                    <span>{models?.find(m => m?.id === message?.model)?.name}</span>
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-muted rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <div className="animate-spin">
                  <Icon name="Loader2" size={16} />
                </div>
                <span className="text-sm text-muted-foreground">Thinking...</span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      {/* Suggested Queries */}
      {messages?.length <= 1 && (
        <div className="p-3 border-t border-border">
          <p className="text-xs text-muted-foreground mb-2">Suggested questions:</p>
          <div className="space-y-1">
            {suggestedQueries?.map((query, index) => (
              <button
                key={index}
                onClick={() => handleSuggestedQuery(query)}
                className="w-full text-left text-xs p-2 bg-muted hover:bg-muted/80 rounded text-foreground nav-transition"
              >
                {query}
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Input */}
      <div className="p-4 border-t border-border">
        <div className="flex space-x-2">
          <Input
            type="text"
            placeholder="Ask about SAP processes, implementation..."
            value={inputValue}
            onChange={(e) => setInputValue(e?.target?.value)}
            onKeyPress={handleKeyPress}
            className="flex-1"
            disabled={isLoading}
          />
          <Button
            variant="default"
            size="icon"
            onClick={handleSendMessage}
            disabled={!inputValue?.trim() || isLoading}
          >
            <Icon name="Send" size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AISidecar;